import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';

interface Template {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components: any[];
}

interface Contact {
  id: string;
  phoneNumber: string;
  name?: string;
  avatar?: string;
}

interface BroadcastTemplateProps {
  template: Template;
  onClose: () => void;
  onSent: () => void;
}

const BroadcastTemplate: React.FC<BroadcastTemplateProps> = ({ template, onClose, onSent }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showVariableForm, setShowVariableForm] = useState(false);
  const [variables, setVariables] = useState<string[]>([]);
  const [variableFields, setVariableFields] = useState<string[]>([]);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadContacts();
  }, []);

  // Debounce search - reload contacts when search term changes
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      loadContacts();
    }, 300); // Wait 300ms after user stops typing
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchTerm]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      // axios already has /api as baseURL
      let url = '/contacts?limit=200'; // Load up to 200 contacts for broadcast
      if (searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      
      const response = await axios.get(url);
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleContact = (contactId: string) => {
    if (selectedContacts.includes(contactId)) {
      setSelectedContacts(selectedContacts.filter(id => id !== contactId));
    } else {
      setSelectedContacts([...selectedContacts, contactId]);
    }
  };

  const handleSendToSelected = () => {
    if (selectedContacts.length === 0) {
      alert('Please select at least one contact');
      return;
    }
    
    // Initialize variables based on template
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    const bodyText = bodyComponent?.text || '';
    const matches = bodyText.match(/\{\{\d+\}\}/g);
    const varCount = matches ? matches.length : 0;
    
    setVariables(Array(varCount).fill(''));
    setVariableFields(Array(varCount).fill(''));
    setShowVariableForm(true);
  };

  const handleBroadcast = async () => {
    if (variables.some(v => !v.trim() && !variableFields[variables.indexOf(v)])) {
      alert('Please fill in all variables or select a field');
      return;
    }

    setSending(true);
    try {
      // axios already has /api as baseURL
      const response = await axios.post('/templates/broadcast', {
        contactIds: selectedContacts,
        templateName: template.name,
        languageCode: template.language,
        variableFields: variableFields.map((field, index) => 
          field || variables[index] // Use field placeholder or manual value
        )
      });

      if (response.data.success) {
        const { sent, failed, total } = response.data.summary;
        alert(`Broadcast complete!\n✅ Sent: ${sent}\n❌ Failed: ${failed}\nTotal: ${total}`);
        onSent();
      }
    } catch (error) {
      console.error('Error broadcasting template:', error);
      alert('Failed to broadcast template');
    } finally {
      setSending(false);
    }
  };

  const handleVariableChange = (index: number, value: string, isField: boolean = false) => {
    if (isField) {
      const newFields = [...variableFields];
      newFields[index] = value;
      setVariableFields(newFields);
      // Clear manual value when field is selected
      const newVars = [...variables];
      newVars[index] = '';
      setVariables(newVars);
    } else {
      const newVars = [...variables];
      newVars[index] = value;
      setVariables(newVars);
      // Clear field when manual value is entered
      const newFields = [...variableFields];
      newFields[index] = '';
      setVariableFields(newFields);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const getTemplateBody = (): string => {
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    return bodyComponent?.text || '';
  };

  const getPreview = (): string => {
    let preview = getTemplateBody();
    variableFields.forEach((field, index) => {
      const value = field || variables[index] || `{{${index + 1}}}`;
      const displayValue = field ? `[${field}]` : value;
      preview = preview.replace(`{{${index + 1}}}`, displayValue);
    });
    return preview;
  };

  return (
    <>
      <div className="assignment-modal-overlay" onClick={onClose}>
        <div className="assignment-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh' }}>
          <div className="assignment-header">
            <h4>{showVariableForm ? 'Configure Variables' : `Send Template: ${template.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`}</h4>
            <button onClick={onClose} className="close-button">×</button>
          </div>

          <div className="assignment-content" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {showVariableForm ? (
              <>
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#dcfce7', borderRadius: '8px', fontSize: '13px', color: '#166534' }}>
                  ✨ Mail Merge: Select contact fields to personalize for each recipient
                </div>

                {variables.map((value, index) => (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
                      Variable {index + 1}
                    </label>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <select
                        value={variableFields[index]}
                        onChange={(e) => handleVariableChange(index, e.target.value, true)}
                        className="form-input"
                        style={{ flex: 1 }}
                      >
                        <option value="">-- Or select contact field --</option>
                        <option value="{{firstName}}">📝 First Name (personalized)</option>
                        <option value="{{lastName}}">📝 Last Name (personalized)</option>
                        <option value="{{name}}">📝 Full Name (personalized)</option>
                        <option value="{{phoneNumber}}">📱 Phone Number (personalized)</option>
                      </select>
                    </div>
                    <input
                      type="text"
                      value={variables[index]}
                      onChange={(e) => handleVariableChange(index, e.target.value, false)}
                      placeholder="Or enter same value for all"
                      className="form-input"
                      disabled={!!variableFields[index]}
                    />
                  </div>
                ))}

                <div style={{ marginTop: '20px' }}>
                  <h5 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#111827' }}>
                    Preview (first contact)
                  </h5>
                  <div style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', padding: '12px' }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}>
                      <p style={{ fontSize: '13px', whiteSpace: 'pre-wrap', margin: 0, lineHeight: '1.5' }}>
                        {getPreview()}
                      </p>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                    💡 Fields in [brackets] will be personalized for each contact
                  </div>
                </div>
              </>
            ) : (
              <>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', fontSize: '13px', color: '#1e40af' }}>
              📱 Select contacts to send this template to
            </div>

            <div className="search-container" style={{ marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Search contacts..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Select All */}
            {contacts.length > 0 && (
              <div 
                onClick={() => {
                  if (selectedContacts.length === contacts.length) {
                    setSelectedContacts([]);
                  } else {
                    setSelectedContacts(contacts.map(c => c.id));
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedContacts.length === contacts.length && contacts.length > 0}
                  onChange={() => {}}
                  style={{ cursor: 'pointer' }}
                />
                <span>Select All ({contacts.length})</span>
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                Loading contacts...
              </div>
            ) : contacts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                No contacts found
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => toggleContact(contact.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: selectedContacts.includes(contact.id) ? '#eff6ff' : 'white',
                      borderColor: selectedContacts.includes(contact.id) ? '#3b82f6' : '#e5e7eb'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => {}}
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="avatar" style={{ width: '40px', height: '40px' }}>
                      {contact.avatar ? (
                        <img src={contact.avatar} alt={contact.name || contact.phoneNumber} />
                      ) : (
                        getInitials(contact.name || contact.phoneNumber)
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>
                        {contact.name || contact.phoneNumber}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {contact.phoneNumber}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
              </>
            )}
          </div>

          <div className="assignment-actions">
            {showVariableForm ? (
              <>
                <button 
                  onClick={() => setShowVariableForm(false)} 
                  className="cancel-button"
                >
                  Back
                </button>
                <button
                  onClick={handleBroadcast}
                  className="assign-button"
                  disabled={sending}
                >
                  {sending ? 'Sending...' : `Send to ${selectedContacts.length} contact${selectedContacts.length !== 1 ? 's' : ''}`}
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
                </div>
                <button onClick={onClose} className="cancel-button">
                  Cancel
                </button>
                <button
                  onClick={handleSendToSelected}
                  className="assign-button"
                  disabled={selectedContacts.length === 0 || sending}
                >
                  Continue
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BroadcastTemplate;
